import { Button } from '@/components/UI/Button'
import { Checkbox } from '@/components/UI/Checkbox'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

type Props = {
  /** 撮影画像の DataURL（data:image/jpeg;base64,...） */
  imageDataUrl: string
  /** 「送信する」押下時 */
  onSubmit: () => void
  /** 「再撮影する」押下時 */
  onRetake: () => void
  /** 閉じる（中断）押下時 */
  onClose: () => void
}

/**
 * OCR 撮影画像プレビュー
 * 撮影直後に画像を確認させ、送信 / 再撮影を選択させる。
 * ピンぼけ・見切れの注意喚起チェックボックスに同意しないと「送信する」は押せない。
 *
 * ブラウザ／端末バック制御:
 *   OcrFrame が撮影遷移時に残したマーカー付きダミー history エントリ（`state.ocrOverlay`）を引き継ぎ、
 *   popstate を検知したら onClose（OCR_PREVIEW_CLOSE）を送出してスポット詳細へ戻す。自前では pushState しない。
 */
export const OcrPreview = ({ imageDataUrl, onSubmit, onRetake, onClose }: Props) => {
  const { t } = useTranslation()

  // ピンぼけ・見切れの注意喚起への同意。同意するまで「送信する」は非活性。
  const [confirmed, setConfirmed] = useState(false)

  // 常に最新の onClose を参照するための ref
  const onCloseRef = useRef(onClose)
  useEffect(() => {
    onCloseRef.current = onClose
  }, [onClose])

  // popstate（バック）によって既に閉じたか
  const poppedRef = useRef(false)
  // 撮り直し時は OcrFrame が再マウントして自前の履歴を積むため、こちらの履歴掃除（back）はスキップする
  // （ここで back() すると再マウントした OcrFrame の popstate を誤発火させてしまう）。
  const skipHistoryCleanupRef = useRef(false)

  useEffect(() => {
    const handlePopState = () => {
      if (poppedRef.current) return
      poppedRef.current = true
      onCloseRef.current?.()
    }
    window.addEventListener('popstate', handlePopState)

    return () => {
      window.removeEventListener('popstate', handlePopState)
      // バック以外（送信 / 閉じる）で離脱した場合は、引き継いだダミーエントリを戻して履歴を掃除する。
      // OcrFrame が積んだマーカーが残っている場合のみ back し、通常の履歴まで戻さないようガードする。
      // 撮り直し時（skipHistoryCleanupRef）は OcrFrame が再マウントしてマーカーを引き継ぐため back しない。
      if (!poppedRef.current && !skipHistoryCleanupRef.current && window.history.state?.ocrOverlay) {
        window.history.back()
      }
    }
  }, [])

  const handleRetake = () => {
    skipHistoryCleanupRef.current = true
    onRetake()
  }

  return (
    <div className="fixed inset-0 z-[100] flex flex-col bg-white">
      {/* プレビュー画像（上・object-contain） */}
      <div className="flex flex-1 items-center justify-center overflow-hidden bg-black">
        <img
          src={imageDataUrl}
          alt={t('spot_detail.ocr.preview.alt', '撮影した画像')}
          className="h-full w-full object-contain"
        />
      </div>

      {/* ピンぼけ・見切れの注意喚起チェックボックス */}
      <div className="flex flex-shrink-0 justify-center px-4 pt-5">
        <Checkbox
          checked={confirmed}
          onChange={setConfirmed}
          label={t('spot_detail.ocr.preview.confirm_message', 'ピンぼけ・見切れがないことを確認しました')}
          labelClassName="text-[16px]"
        />
      </div>

      {/* ボタン行: 再撮影する | 送信する（横並び・等幅 fullWidth。同意するまで送信は非活性） */}
      <div className="flex gap-4 px-4 py-6">
        <Button
          label={t('spot_detail.ocr.preview.retake', '再撮影する')}
          variant="system"
          size="md"
          fullWidth
          onClick={handleRetake}
        />
        <Button
          label={t('spot_detail.ocr.preview.submit', '送信する')}
          variant="primary"
          size="md"
          fullWidth
          disabled={!confirmed}
          onClick={onSubmit}
        />
      </div>
    </div>
  )
}
